/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Table, message, Tag, Space, Button, Input, Card, Row, Col, Modal, Form, Tabs, Switch, Select, List } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HomeOutlined, MedicineBoxOutlined, TeamOutlined, EnvironmentOutlined, CalendarOutlined, CheckCircleOutlined, UserOutlined, SettingOutlined, FileTextOutlined } from '@ant-design/icons';
import { settingsService } from '../../../services/settingsService';
import { searchnormal } from '../../../components/imagepath';
import FiscalTab from '../FiscalTab';

const { Search } = Input;

    const iconMap = {
    'home': <HomeOutlined />,
    'status': <CheckCircleOutlined />,
    'stethoscope': <MedicineBoxOutlined />,
    'bed': <SettingOutlined />,
    'square': <SettingOutlined />,
    'id-card': <UserOutlined />,
    'users': <TeamOutlined />,
    'globe': <EnvironmentOutlined />,
    'hospital': <SettingOutlined />,
    'calendar': <CalendarOutlined />,
    'check-circle': <CheckCircleOutlined />,
    'user-friends': <TeamOutlined />,
    'fiscal': <FileTextOutlined />,
};

const fieldLabels = {
    specialties: {
        name: 'Nombre',
        description: 'Descripción',
    },
    appointment_types: {
        name: 'Nombre',
        duration: 'Duración (min)',
        color: 'Color',
    },
    appointment_statuses: {
        name: 'Nombre',
        color: 'Color',
    },
    identification_types: {
        name: 'Nombre',
        code: 'Código',
    },
    civil_states: {
        name: 'Nombre',
        code: 'Código',
        is_active: 'Activo',
    },
    nationalities: {
        name: 'Nombre',
        code: 'Código',
        is_active: 'Activo',
    },
    hospitals: {
        name: 'Nombre',
        code: 'Código',
        address: 'Dirección',
        phone: 'Teléfono',
        email: 'Email',
        is_active: 'Activo',
    },
};

const SettingsManagement = () => {
    const [loading, setLoading] = useState(false);
    const [settingTypes, setSettingTypes] = useState([]);
    const [selectedType, setSelectedType] = useState('specialties');
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalVisible, setModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchSettingTypes();
    }, []);

    useEffect(() => {
        if (selectedType) {
            fetchData();
        }
    }, [selectedType, searchTerm]);

    const fetchSettingTypes = async () => {
        try {
            const types = await settingsService.getTypes();
            setSettingTypes(types);
        } catch (error) {
            console.error('Error fetching setting types:', error);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await settingsService.getAll(selectedType, {
                search: searchTerm || undefined,
            });
            setData(response.data || []);
        } catch (error) {
            console.error('Error fetching data:', error);
            message.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (editingItem) {
                await settingsService.update(selectedType, editingItem.id, values);
                message.success('Registro actualizado');
            } else {
                await settingsService.create(selectedType, values);
                message.success('Registro creado');
            }
            setModalVisible(false);
            form.resetFields();
            setEditingItem(null);
            fetchData();
        } catch (error) {
            console.error('Error saving:', error);
            message.error('Error al guardar');
        }
    };

    const handleEdit = (record) => {
        setEditingItem(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (id: number) => {
        try {
            await settingsService.delete(selectedType, id);
            message.success('Registro eliminado');
            fetchData();
        } catch (error) {
            console.error('Error deleting:', error);
            message.error('Error al eliminar');
        }
    };

    const getColumns = () => {
        const labels = fieldLabels[selectedType] || {};
        const columns = [];

        Object.keys(labels).forEach((key) => {
            if (key === 'is_active') {
                columns.push({
                    title: labels[key],
                    dataIndex: key,
                    key: key,
                    render: (val: boolean) => val ? <Tag color="green">Sí</Tag> : <Tag>No</Tag>,
                });
            } else if (key === 'is_final') {
                columns.push({
                    title: labels[key],
                    dataIndex: key,
                    key: key,
                    render: (val: boolean) => val ? <Tag color="blue">Sí</Tag> : <Tag>No</Tag>,
                });
            } else if (key === 'room_id') {
                columns.push({
                    title: labels[key],
                    dataIndex: 'room',
                    key: key,
                    render: (room) => room?.name || 'N/A',
                });
            } else {
                columns.push({
                    title: labels[key],
                    dataIndex: key,
                    key: key,
                });
            }
        });

        columns.push({
            title: 'Acciones',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
                </Space>
            ),
        });

        return columns;
    };

    const getFormFields = () => {
        const labels = fieldLabels[selectedType] || {};
        const fields = [];

        Object.keys(labels).forEach((key) => {
            if (key === 'is_active' || key === 'is_final') {
                fields.push(
                    <Form.Item key={key} name={key} label={labels[key]} valuePropName="checked">
                        <Switch />
                    </Form.Item>
                );
            } else if (key === 'room_id') {
                // For beds - would need to load rooms
                fields.push(
                    <Form.Item key={key} name={key} label={labels[key]}>
                        <Input type="number" />
                    </Form.Item>
                );
            } else if (key === 'duration') {
                fields.push(
                    <Form.Item key={key} name={key} label={labels[key]}>
                        <Input type="number" />
                    </Form.Item>
                );
            } else {
                fields.push(
                    <Form.Item key={key} name={key} label={labels[key]} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                );
            }
        });

        return fields;
    };

    // Task 5.4: append a Fiscal tab to the catalog tabs. Fiscal settings
    // are a single record (one per doctor), so they get a dedicated
    // sub-component instead of the generic Table+Modal flow.
    const fiscalTab = {
        key: 'fiscal',
        label: (
            <span>
                <FileTextOutlined /> Fiscal
            </span>
        ),
        children: <FiscalTab />,
    };

    const tabItems = settingTypes.map((type) => ({
        key: type.key,
        label: (
            <span>
                {iconMap[type.icon] || <SettingOutlined />} {type.label}
            </span>
        ),
        children: (
            <>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={8}>
                        <Search
                            placeholder="Buscar..."
                            onSearch={setSearchTerm}
                            enterButton={<img src={searchnormal} alt="Buscar" />}
                            allowClear
                        />
                    </Col>
                    <Col span={8}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setEditingItem(null);
                                form.resetFields();
                                setModalVisible(true);
                            }}
                        >
                            Agregar
                        </Button>
                    </Col>
                </Row>
                <Table
                    columns={getColumns()}
                    dataSource={data}
                    loading={loading}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                />
            </>
        ),
    })).concat([fiscalTab]);

    return (
        <>
                    <div className="page-header">
                        <h3>Configuración del Sistema</h3>
                    </div>
                    <Card>
                        <Tabs
                            activeKey={selectedType}
                            onChange={setSelectedType}
                            type="card"
                            items={tabItems}
                            tabPosition="left"
                            style={{ minHeight: 400 }}
                        />
                    </Card>

            <Modal
                title={editingItem ? `Editar ${settingTypes.find(t => t.key === selectedType)?.label || 'Registro'}` : `Nuevo ${settingTypes.find(t => t.key === selectedType)?.label || 'Registro'}`}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                    setEditingItem(null);
                }}
                onOk={handleSave}
                width={500}
            >
                <Form form={form} layout="vertical">
                    {getFormFields()}
                </Form>
            </Modal>
        </>
    );
};

export default SettingsManagement;
